/**
 * The Channel Deep-dive's scope: the sidebar workspace or, for a platform
 * master, every listed workspace, exactly as D1's ExecutiveOverview (its
 * R10); plus D1's all-workspaces view arriving on a link as scope=all (I2,
 * option a).
 *
 * - The all-workspaces switch is page-local state, as on D1. It survives a
 *   channel tab (both routes render one element, which react-router keeps).
 *   Only a REAL workspace change, from one workspace to another, turns it off
 *   (R19); the first workspace arriving (null → id) does not.
 * - scope=all asks for that view. A master gets it, anyone else keeps one
 *   workspace. While the master status is still being read the scope is null,
 *   so nothing is read for a workspace the viewer may not end up on. The view
 *   follows the key during render, so no single-workspace read starts in the
 *   render between the status arriving and the switch being set.
 * - useScopeArrival, on the channel page only, then sets the switch for a
 *   master and takes the key out of the URL.
 *
 * @module hooks/useChannelPageScope
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { scopeAllAsked, withoutScope, writeShared, type SharedFilters } from '@/services/dashboardFilters';
import { useIsPlatformMaster } from './useIsPlatformMaster';
import type { OverviewScope } from './useOverviewData';

export interface PageScope {
  workspaceId: string | null;
  workspaceName: string | null;
  /** Workspace id → name, for the all-workspaces campaign names (R25). */
  workspaceNames: Readonly<Record<string, string>>;
  isMaster: boolean;
  /** The master status is still being read. */
  masterLoading: boolean;
  /** The URL still carries scope=all (I2): asked for, not applied yet. */
  scopeAllAsked: boolean;
  /** The switch as drawn: set by the viewer, or asked for by the URL. */
  viewAll: boolean;
  setViewAll: (on: boolean) => void;
  showAll: boolean;
  /** null = nothing to read yet: no sidebar workspace, or scope=all waiting on the master status. */
  scope: OverviewScope | null;
}

const REPLACE = { replace: true } as const;

export const useChannelPageScope = (): PageScope => {
  const { workspace, workspaces } = useWorkspace();
  const { isMaster, loading: masterLoading } = useIsPlatformMaster(true);
  const [params] = useSearchParams();
  const asked = scopeAllAsked(params);
  const [switchedOn, setViewAll] = useState(false);
  const workspaceId = workspace?.id ?? null;
  // R19: off on a real workspace change only. The ref holds the last workspace seen, so a mount (StrictMode
  // runs this twice) and null → id change nothing, and id → null → another id still counts as a change.
  const lastWorkspace = useRef(workspaceId);
  useEffect(() => {
    if (workspaceId === null) return;
    const before = lastWorkspace.current;
    lastWorkspace.current = workspaceId;
    if (before !== null && before !== workspaceId) setViewAll(false);
  }, [workspaceId]);
  const viewAll = switchedOn || asked;
  const showAll = isMaster && viewAll;
  const pending = asked && masterLoading;
  const idsKey = workspaces.map((w) => w.id).join(',');
  const scope = useMemo<OverviewScope | null>(() => {
    if (pending) return null;
    if (showAll) return { workspaceIds: idsKey ? idsKey.split(',') : [], workspaceId: null };
    return workspaceId ? { workspaceIds: [workspaceId], workspaceId } : null;
  }, [pending, showAll, idsKey, workspaceId]);
  const workspaceNames = useMemo(() => Object.fromEntries(workspaces.map((w) => [w.id, w.name])), [workspaces]);
  return {
    workspaceId, workspaceName: workspace?.name ?? null, workspaceNames, isMaster, masterLoading,
    scopeAllAsked: asked, viewAll, setViewAll, showAll, scope,
  };
};

/**
 * I2 on the channel page: once the master status is known, a master's switch
 * goes on, and the key leaves the URL for everyone, by ONE replace that also
 * writes the shared filters in force. useDashboardFilters may rewrite the URL
 * in the same tick (on mount), and react-router's setter builds on the last
 * render's URL, so the later write wins: this one runs later and carries that
 * rewrite too. It reruns on any URL that still holds the key, whatever the
 * order. Never called on /channels: a search-only replace there would race
 * <Navigate> (see the page's useSharedSearch).
 */
export const useScopeArrival = (page: PageScope, shared: SharedFilters): void => {
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const { scopeAllAsked: asked, masterLoading, isMaster, setViewAll } = page;
  const { period, objective, currency } = shared;
  useEffect(() => {
    if (!asked || masterLoading) return;
    if (isMaster) setViewAll(true);
    setParams((prev) => writeShared(withoutScope(prev), { period, objective, currency }), REPLACE);
  }, [asked, masterLoading, isMaster, setViewAll, setParams, search, period, objective, currency]);
};
